import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PostLoginVideo.css';

export const POST_LOGIN_VIDEO_PATH = '/post-login-video';
export const POST_LOGIN_VIDEO_SRC = '/CODA.mp4';

const HOLD_SKIP_MS = 3000;
const HOLD_RING_RADIUS = 24;
const HOLD_RING_CIRCUMFERENCE = 2 * Math.PI * HOLD_RING_RADIUS;

function PostLoginVideo({ onComplete }) {
  const [showVideo, setShowVideo] = useState(true);
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const videoRef = useRef(null);
  const hasFinishedRef = useRef(false);
  const holdStartRef = useRef(null);
  const holdRafRef = useRef(null);

  const finish = useCallback(() => {
    if (hasFinishedRef.current) return;
    hasFinishedRef.current = true;
    setShowVideo(false);
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  const cancelHold = useCallback(() => {
    holdStartRef.current = null;
    if (holdRafRef.current != null) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    setIsHolding(false);
    setHoldProgress(0);
  }, []);

  const startHold = useCallback(
    (event) => {
      if (hasFinishedRef.current) return;
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      if (event.currentTarget.setPointerCapture) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          /* ignore if capture unsupported */
        }
      }
      cancelHold();
      holdStartRef.current = performance.now();
      setIsHolding(true);

      const tick = (now) => {
        if (holdStartRef.current == null) return;
        const elapsed = now - holdStartRef.current;
        const progress = Math.min(1, elapsed / HOLD_SKIP_MS);
        setHoldProgress(progress);
        if (progress >= 1) {
          cancelHold();
          finish();
          return;
        }
        holdRafRef.current = requestAnimationFrame(tick);
      };

      holdRafRef.current = requestAnimationFrame(tick);
    },
    [cancelHold, finish]
  );

  useEffect(() => () => cancelHold(), [cancelHold]);

  useEffect(() => {
    if (!isHolding) return undefined;
    const endHold = () => cancelHold();
    window.addEventListener('pointerup', endHold);
    window.addEventListener('pointercancel', endHold);
    return () => {
      window.removeEventListener('pointerup', endHold);
      window.removeEventListener('pointercancel', endHold);
    };
  }, [isHolding, cancelHold]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc' && event.keyCode !== 27) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      cancelHold();
      finish();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [finish, cancelHold]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const attemptPlay = () => {
      const playPromise = video.play();
      if (playPromise === undefined) return;
      playPromise.catch(() => {
        video.muted = true;
        video
          .play()
          .then(() => {
            setTimeout(() => {
              video.muted = false;
            }, 100);
          })
          .catch(() => {});
      });
    };

    const setupMaxDuration = () => {
      if (video.duration && video.duration > 0) {
        const maxTime = video.duration * 1000 + 2000;
        return setTimeout(() => {
          if (!hasFinishedRef.current) {
            console.warn('Post-login video max duration reached, continuing');
            finish();
          }
        }, maxTime);
      }
      return setTimeout(() => {
        if (!hasFinishedRef.current) {
          console.warn('Post-login video max duration reached, continuing');
          finish();
        }
      }, 15000);
    };

    let maxDurationTimeout = setupMaxDuration();

    const onDurationChange = () => {
      if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
      maxDurationTimeout = setupMaxDuration();
    };
    video.addEventListener('durationchange', onDurationChange);

    if (video.readyState >= 2) {
      attemptPlay();
    } else {
      const onLoadedData = () => {
        attemptPlay();
        if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
        maxDurationTimeout = setupMaxDuration();
      };
      video.addEventListener('loadeddata', onLoadedData, { once: true });
      video.load();

      return () => {
        if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('durationchange', onDurationChange);
      };
    }

    return () => {
      if (maxDurationTimeout) clearTimeout(maxDurationTimeout);
      video.removeEventListener('durationchange', onDurationChange);
    };
  }, [finish]);

  const handleVideoError = () => {
    console.warn('Post-login video failed to load, continuing');
    finish();
  };

  if (!showVideo) {
    return null;
  }

  const progressOffset = HOLD_RING_CIRCUMFERENCE * (1 - holdProgress);

  return (
    <div
      data-post-login-video-overlay
      className="post-login-video-overlay"
      role="presentation"
      onPointerDown={startHold}
      onPointerUp={cancelHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
      onContextMenu={(e) => e.preventDefault()}
    >
      <video
        ref={videoRef}
        className="post-login-video-player"
        src={POST_LOGIN_VIDEO_SRC}
        onEnded={finish}
        onError={handleVideoError}
        playsInline
        muted={false}
        loop={false}
        autoPlay
        preload="auto"
      />
      <div
        className={`post-login-video-hold-skip${isHolding ? ' is-holding' : ''}`}
        aria-hidden="true"
      >
        <svg viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
          <circle
            className="post-login-video-hold-skip-track"
            cx="28"
            cy="28"
            r={HOLD_RING_RADIUS}
          />
          <circle
            className="post-login-video-hold-skip-progress"
            cx="28"
            cy="28"
            r={HOLD_RING_RADIUS}
            strokeDasharray={HOLD_RING_CIRCUMFERENCE}
            strokeDashoffset={progressOffset}
          />
        </svg>
      </div>
    </div>
  );
}

export default PostLoginVideo;
